import React from 'react';
import { NamedLink } from '../../components';
import {
  stateFromSlug,
  otherStates,
  stateGuideBlog,
} from '../../util/interlinking';

import css from './StateRelatedLinks.module.css';

/**
 * SEO internal-linking block for a state hub (StateListsPage, /states/:state).
 * The page itself already lists this state's cities, so this block adds the
 * two links a state hub was missing:
 *   - "[State] Trailer Towing Laws" guide blog (state -> blog)
 *   - "Browse Trailer Rentals by State" (state -> other state hubs), so hubs
 *     cross-link each other instead of dead-ending laterally.
 *
 * Rendered server-side (data hydrated in loadStateData). Fully defensive.
 */
const StateRelatedLinks = props => {
  const { stateSlug, states = [], blogs = [] } = props;
  if (!stateSlug) return null;

  const stateObj = stateFromSlug(stateSlug, states);
  // Derive a readable state name even if the object lookup misses.
  const stateName =
    (stateObj && stateObj.title) ||
    String(stateSlug)
      .replace(/^trailer-rentals-/, '')
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  const guide = stateObj ? stateGuideBlog(stateObj, blogs, states) : null;
  const others = otherStates(stateObj || { slug: stateSlug }, states, 8).filter(
    s => s && s.slug
  );

  const hasGuide = !!(guide && guide.slug);
  const hasOthers = others.length > 0;
  if (!hasGuide && !hasOthers) return null;

  return (
    <aside className={css.related} aria-label="Related trailer rental links">
      {hasGuide ? (
        <div className={css.block}>
          <h2 className={css.blockTitle}>Trailer Towing Guides</h2>
          <ul className={css.linkList}>
            <li>
              <NamedLink
                name="SingleBlogPage"
                params={{ title: guide.slug }}
                className={css.link}
              >
                {guide.title || `${stateName} trailer towing laws`}
              </NamedLink>
            </li>
          </ul>
        </div>
      ) : null}

      {hasOthers ? (
        <div className={css.block}>
          <h2 className={css.blockTitle}>Browse Trailer Rentals by State</h2>
          <ul className={css.linkList}>
            {others.map(s => (
              <li key={s.slug}>
                <NamedLink
                  name="StateListsPage"
                  params={{ state: s.slug }}
                  className={css.link}
                >
                  Trailer rentals in {s.title}
                </NamedLink>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
};

export default StateRelatedLinks;
